import { useState, FormEvent } from 'react';

/**
 * Newsletter form state and submission logic
 */
export interface NewsletterFormState {
  email: string;
  submitted: boolean;
  loading: boolean;
  error: string | null;
  setEmail: (email: string) => void;
  handleSubmit: (e: FormEvent) => Promise<void>;
}

/**
 * API response from newsletter subscription endpoint
 */
interface SubscribeResponse {
  success: boolean;
  message: string;
}

/**
 * Hook to manage newsletter subscription form state and logic
 *
 * Makes API call to /api/subscribe endpoint which integrates with Buttondown.
 *
 * Usage:
 *   const { email, submitted, loading, error, setEmail, handleSubmit } = useNewsletterForm();
 */
export function useNewsletterForm(): NewsletterFormState {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Reset states
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data: SubscribeResponse = await response.json();

      if (response.ok && data.success) {
        // Success!
        setSubmitted(true);
        setEmail('');

        // Reset success message after 3 seconds
        setTimeout(() => {
          setSubmitted(false);
        }, 3000);
      } else {
        // API returned an error
        setError(data.message || 'Failed to subscribe. Please try again.');
      }
    } catch (err) {
      // Network error or other exception
      console.error('Newsletter subscription error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    submitted,
    loading,
    error,
    setEmail,
    handleSubmit
  };
}
